import { Model, Participant  } from '@bimdata/definitions/dto';
import adminApi from './api';
import castDates from '@bimdata/client/utils/cast-dates';
import { ApiError } from '@bimdata/client';
import { ProjectDeletionMode } from '@bimdata/definitions/const';
import { UserStatus } from '@bimdata/definitions/const';
import qs from 'qs';

import { AdminedProject } from '../types/admined-project.dto';
import { AdminedUser } from '../types/admined-user.dto';
import { FetchForAdminResult } from '../types/fetch-for-admin-result.dto';

async function authorize(): Promise<void> {
  const { data: isSuperuser } = await adminApi().get<boolean>('/authorization');
  if (!isSuperuser) {
    throw new ApiError(403, 'Access denied.');
  }
}

async function getProjects({ search, archivedFilter, idsFilter, sortBy: sort_by, sortOrder: sort_order, offset, limit, total, stats }: {
  search?: string,
  archivedFilter?: boolean,
  idsFilter?: number[],
  sortBy?: keyof AdminedProject,
  sortOrder?: 'ASC' | 'DESC',
  offset?: number,
  limit?: number,
  total?: boolean,
  stats?: boolean,
} = {}): Promise<FetchForAdminResult<AdminedProject, 'active' | 'archived'>> {
  const { data } = await adminApi().get<FetchForAdminResult<AdminedProject, 'active' | 'archived'>>('projects/list', {
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
    params: {
      search,
      archivedFilter,
      idsFilter,
      sort_by,
      sort_order,
      offset,
      limit,
      total,
      stats,
    }
  });
  return {
    page: data.page.map((p) => castDates<AdminedProject>(p, [ 'created', 'updated', 'archived' ])),
    total: data.total,
    stats: data.stats,
  };
}

async function getProjectParticipants(id: number): Promise<Array<Participant>> {
  const { data } = await adminApi().get<Array<Participant>>(`projects/${id}/participants`);
  return data;
}

async function getProjectModels(id: number): Promise<Array<Model>> {
  const { data } = await adminApi().get<Array<Model>>(`projects/${id}/models`);
  return data.map((m) => castDates<Model>(m, [ 'loaded' ]));
}

async function deleteProjects(ids: number[], mode: ProjectDeletionMode): Promise<Record<string, string> | void> {
  const { data } = await adminApi().delete<Record<string, string> | void>(`projects?mode=${mode}`, { data: { projectList: ids } });
  return data;
}

async function getUsers({ search, statusFilter, sortBy: sort_by, sortOrder: sort_order, offset, limit, total, stats, }: {
  search?: string,
  statusFilter?: UserStatus,
  sortBy?: keyof AdminedUser,
  sortOrder?: 'ASC' | 'DESC',
  offset?: number,
  limit?: number,
  total?: boolean,
  stats?: boolean,
} = {}): Promise<FetchForAdminResult<AdminedUser, UserStatus>> {
  const { data } = await adminApi().get<FetchForAdminResult<AdminedUser, UserStatus>>('users/list', {
    params: {
      search,
      statusFilter,
      sort_by,
      sort_order,
      offset,
      limit,
      total,
      stats,
    }
  });
  return {
    page: data.page.map((u) => castDates<AdminedUser>(u, [ 'registered' ])),
    total: data.total,
    stats: data.stats,
  };
}

async function setStatus(ids: string[], status: UserStatus): Promise<string[]> {
  const { data } = await adminApi().put<string[]>('users/status', { ids, status });
  return data;
}

async function deleteUsers(ids: string[]): Promise<string[]> {
  const { data } = await adminApi().delete<string[]>('users', {
    params: { ids },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });
  return data;
}

export {
  getProjects,
  getProjectModels,
  getProjectParticipants,
  deleteProjects,
  authorize,
  getUsers,
  setStatus,
  deleteUsers,
};